import React, { useEffect, useRef, useState } from 'react';
import { 
  Terminal, 
  Loader2, 
  Play, 
  Pause,
  SkipBack,
  SkipForward,
  ChevronUp,
  ChevronDown,
  CheckCircle2, 
  XCircle, 
  RotateCcw, 
  Square,
  Copy,
  Check,
  FileText
} from 'lucide-react';
import type { EcadAutomacaoPasso } from './types';
import { useEcadStore } from './store/ecadStore';


interface EcadSimulationSectionProps {
  passos: EcadAutomacaoPasso[];
  isSimulating: boolean;
  isPaused: boolean;
  simulationLogs: string[];
  simulationSuccess: boolean | null;
  simulationModeVisible: boolean;
  setSimulationModeVisible: (val: boolean) => void;
  handleRunSimulation: () => Promise<void>;
  
  streamFrame: string | null;
  currentBrowserUrl: string;
  handleStopSimulation: () => void;
  activeStepOrdem: number | null;

  handlePauseSimulation: () => void;
  handleResumeSimulation: () => void;
  handleStepForward: () => void;
  handleStepBackward: () => void;
  browserInstance: { active: boolean; headless: boolean | null; startTime: number | null } | null;
  handleKillBrowser: () => void;
  handleRestartBrowser: () => void;
  pdfUrl: string | null;
  videoUrl: string | null;
  generatingPdf: boolean;
  pdfProgressError: string | null;
  handleGeneratePdf: (silent?: boolean) => Promise<void>;
  setSimulationSuccess: (val: boolean | null) => void;
  precacheInfo: { active: boolean; total: number; completed: number } | null;
}

export const EcadSimulationSection: React.FC<EcadSimulationSectionProps> = ({
  passos,
  isSimulating,
  isPaused,
  simulationLogs,
  simulationSuccess,
  simulationModeVisible,
  setSimulationModeVisible,
  handleRunSimulation,
  streamFrame,
  currentBrowserUrl,
  handleStopSimulation,
  activeStepOrdem,
  handlePauseSimulation,
  handleResumeSimulation,
  handleStepForward,
  handleStepBackward,
  browserInstance,
  handleKillBrowser,
  handleRestartBrowser,
  pdfUrl,
  videoUrl,
  generatingPdf,
  pdfProgressError,
  handleGeneratePdf,
  setSimulationSuccess,
  precacheInfo
}) => {
  const terminalEndRef = useRef<HTMLDivElement>(null);
  const [isConsoleExpanded, setIsConsoleExpanded] = useState(false);
  const prevIsSimulating = useRef(isSimulating);
  const hasCollapsedOnFrame = useRef(false);

  const [showInstanceMenu, setShowInstanceMenu] = useState(false);
  const [uptimeStr, setUptimeStr] = useState<string>('0s');
  const [copied, setCopied] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (isSimulating) {
      if (canvasRef.current) {
        useEcadStore.getState().setStreamCanvas(canvasRef.current);
      }
      return () => {
        useEcadStore.getState().setStreamCanvas(null);
      };
    }
  }, [isSimulating]);


  const handleCopyLogs = (e: React.MouseEvent) => {
    e.stopPropagation();
    const allLogs = simulationLogs.join('\n');
    navigator.clipboard.writeText(allLogs).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error('Falha ao copiar logs:', err);
    });
  };

  // Dynamic Uptime counter ticker
  useEffect(() => {
    if (browserInstance?.active && browserInstance.startTime) {
      const updateUptime = () => {
        const diffSeconds = Math.floor((Date.now() - browserInstance.startTime!) / 1000);
        if (diffSeconds < 60) {
          setUptimeStr(`${diffSeconds}s`);
        } else {
          const mins = Math.floor(diffSeconds / 60);
          const secs = diffSeconds % 60;
          setUptimeStr(`${mins}m ${secs}s`);
        }
      };

      const interval = setInterval(updateUptime, 1000);
      updateUptime();

      return () => {
        clearInterval(interval);
        setUptimeStr('0s');
      };
    }
  }, [browserInstance]);

  // Click-away listener to automatically dismiss the instance menu dropdown
  useEffect(() => {
    if (!showInstanceMenu) return;
    const handleOutsideClick = () => {
      setShowInstanceMenu(false);
    };
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [showInstanceMenu]);



  // Auto-scroll logs terminal
  useEffect(() => {
    if (isConsoleExpanded) {
      terminalEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [simulationLogs, isConsoleExpanded]);

  // Handle auto expand/collapse based on simulation state changes
  useEffect(() => {
    if (isSimulating && !prevIsSimulating.current) {
      // Simulation just started: open console, reset collapse tracker
      setIsConsoleExpanded(true);
      hasCollapsedOnFrame.current = false;
    } else if (!isSimulating && prevIsSimulating.current) {
      // Simulation stopped
      setIsConsoleExpanded(false);
    }
    prevIsSimulating.current = isSimulating;
  }, [isSimulating]);

  // Auto-collapse console when the first browser stream frame arrives
  useEffect(() => {
    if (isSimulating && streamFrame && !hasCollapsedOnFrame.current) {
      setIsConsoleExpanded(false);
      hasCollapsedOnFrame.current = true;
    }
  }, [streamFrame, isSimulating]);



  const lastLog = simulationLogs[simulationLogs.length - 1] || '';

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      overflow: 'hidden',
      position: 'relative',
      padding: '20px 24px'
    }}>
      
      {/* 2. Console Logs Section (Outside and before the main viewport card) */}
      {isSimulating && (
        <div style={{
          height: isConsoleExpanded ? '160px' : '38px',
          minHeight: isConsoleExpanded ? '160px' : '38px',
          background: '#0f172a',
          border: '1px solid #cbd5e1',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'height 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          zIndex: 10
        }}>
          {/* Console Header */}
          <div 
            onClick={() => setIsConsoleExpanded(!isConsoleExpanded)}
            style={{
              height: '38px',
              minHeight: '38px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px',
              cursor: 'pointer',
              userSelect: 'none',
              background: '#0f172a',
              borderBottom: isConsoleExpanded ? '1px solid #334155' : 'none'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, position: 'relative' }}>
              <Terminal size={14} style={{ color: '#10b981', flexShrink: 0 }} />
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#34d399', flexShrink: 0 }}>
                Console de Execução
              </span>
              <span style={{ fontSize: '0.65rem', color: '#64748b', flexShrink: 0 }}>
                ({simulationLogs.length} logs)
              </span>

              {!isConsoleExpanded && lastLog && (
                <>
                  <span style={{ color: '#475569', fontSize: '0.7rem' }}>•</span>
                  <span style={{ 
                    fontSize: '0.7rem', 
                    color: lastLog.includes('❌') ? '#f87171' : lastLog.includes('🎉') ? '#34d399' : '#94a3b8', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap'
                  }}>
                    {lastLog}
                  </span>
                </>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b' }}>
              <button
                onClick={handleCopyLogs}
                title="Copiar todos os logs"
                style={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  color: copied ? '#34d399' : '#94a3b8',
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease-in-out',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = copied ? '#34d399' : '#f8fafc';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.12)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = copied ? '#34d399' : '#94a3b8';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)';
                }}
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
              </button>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {isConsoleExpanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </div>
            </div>
          </div>

          {/* Console Logs List */}
          {isConsoleExpanded && (
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '12px 16px',
              fontFamily: "'Courier New', Courier, monospace",
              fontSize: '0.72rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              {simulationLogs.map((log, idx) => {
                let color = '#94a3b8';
                if (log.includes('🎉') || log.includes('sucesso')) color = '#34d399';
                if (log.includes('❌') || log.includes('ERRO') || log.includes('Falha')) color = '#f87171';
                if (log.includes('Passo')) color = '#60a5fa';
                if (log.includes('⏸️')) color = '#c084fc';
                return (
                  <div key={idx} style={{ color, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                    {log}
                  </div>
                );
              })}
              {isSimulating && !simulationSuccess && (
                <div style={{ 
                  color: isPaused ? '#c084fc' : '#60a5fa', 
                  animation: isPaused ? 'none' : 'pulse 1.5s infinite', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  marginTop: '4px'
                }}>
                  <Loader2 size={10} className={isPaused ? "" : "spin"} /> 
                  {isPaused ? 'Pausado' : 'Executando automação no servidor...'}
                </div>
              )}
              <div ref={terminalEndRef} />
            </div>
          )}
        </div>
      )}

      {/* Unified Viewport & Controls Container */}
      <div style={{
        flex: 1,
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.02)',
        position: 'relative'
      }}>
        
        {/* 1. Browser Screen Stream Frame */}
        <div style={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8fafc',
          overflow: 'hidden',
          padding: isSimulating && streamFrame ? 0 : '20px'
        }}>
          {precacheInfo && precacheInfo.active ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              color: '#64748b',
              padding: '40px'
            }}>
              <Loader2 size={36} style={{ animation: 'spin 1.5s linear infinite', color: '#a78bfa' }} />
              <div style={{ fontSize: '16px', fontWeight: 600, color: '#334155' }}>
                Pré-gerando áudios da Alice...
              </div>
              <div style={{ fontSize: '13px', color: '#475569' }}>
                Aguarde, não está gravando ainda.
              </div>
              <div style={{
                width: '280px',
                height: '6px',
                borderRadius: '3px',
                background: '#cbd5e1',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${precacheInfo.total > 0 ? (precacheInfo.completed / precacheInfo.total) * 100 : 0}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #7c3aed, #a78bfa)',
                  borderRadius: '3px',
                  transition: 'width 0.5s ease'
                }} />
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                {precacheInfo.completed} / {precacheInfo.total} áudios
              </div>
            </div>
          ) : isSimulating ? (
            <canvas 
              ref={canvasRef}
              width={1280}
              height={720}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                display: 'block',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px'
              }} 
            />

          ) : !isSimulating && simulationSuccess === true ? (
            /* SUCCESS & ARTIFACTS / PUBLISH CARD */
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '24px',
              padding: '30px',
              textAlign: 'center',
              zIndex: 2,
              maxWidth: '560px',
              width: '100%',
              background: '#ffffff',
              border: '1px solid #10b981',
              borderRadius: '16px',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.05)',
              animation: 'fadeIn 0.3s ease-in-out'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#10b981',
                boxShadow: '0 0 20px rgba(16, 185, 129, 0.2)'
              }}>
                <CheckCircle2 size={32} />
              </div>
              
              <div>
                <h4 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px 0', fontFamily: "'Outfit', sans-serif" }}>
                  Simulação Concluída com Sucesso! 🎉
                </h4>
                <p style={{ fontSize: '0.78rem', color: '#475569', margin: 0, lineHeight: 1.45 }}>
                  Todos os passos foram validados e executados sem erros no simulador.
                </p>
              </div>

              {/* Status dos Artefatos de Publicação */}
              <div style={{
                width: '100%',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                textAlign: 'left',
                boxSizing: 'border-box'
              }}>
                <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Verificação de Artefatos para Publicação
                </span>

                {/* Video Status */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.76rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: videoUrl ? '#10b981' : '#f59e0b' }} />
                    <span style={{ color: '#334155', fontWeight: 500 }}>Gravação de Vídeo (.mp4)</span>
                  </div>
                  <span style={{ 
                    fontSize: '0.68rem', 
                    fontWeight: 700, 
                    color: videoUrl ? '#047857' : '#d97706',
                    background: videoUrl ? '#ecfdf5' : '#fef3c7',
                    border: videoUrl ? '1px solid #a7f3d0' : '1px solid #fde68a',
                    padding: '2px 8px',
                    borderRadius: '4px'
                  }}>
                    {videoUrl ? 'Homologado' : 'Vídeo Pendente'}
                  </span>
                </div>

                {/* PDF Status */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.76rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: generatingPdf ? '#a78bfa' : pdfUrl ? '#10b981' : '#ef4444' }} />
                    <span style={{ color: '#334155', fontWeight: 500 }}>Manual de Instruções (PDF)</span>
                  </div>
                  <span style={{ 
                    fontSize: '0.68rem', 
                    fontWeight: 700, 
                    color: generatingPdf ? '#6d28d9' : pdfUrl ? '#047857' : '#b91c1c',
                    background: generatingPdf ? '#f5f3ff' : pdfUrl ? '#ecfdf5' : '#fef2f2',
                    border: generatingPdf ? '1px solid #ddd6fe' : pdfUrl ? '1px solid #a7f3d0' : '1px solid #fecaca',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    {generatingPdf && <Loader2 size={10} className="spin" />}
                    {generatingPdf ? 'Gerando...' : pdfUrl ? 'Homologado' : pdfProgressError ? 'Erro na Geração' : 'PDF Pendente'}
                  </span>
                </div>
              </div>

              {/* Ações */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                {pdfProgressError && !generatingPdf && (
                  <div style={{
                    fontSize: '0.72rem',
                    color: '#991b1b',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    padding: '10px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    textAlign: 'left'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700 }}>
                      <XCircle size={14} style={{ flexShrink: 0 }} />
                      Falha ao gerar manual PDF automaticamente:
                    </div>
                    <div style={{ fontSize: '0.68rem', opacity: 0.85, paddingLeft: '20px' }}>
                      {pdfProgressError}
                    </div>
                  </div>
                )}
                {generatingPdf ? (
                  <div style={{
                    fontSize: '0.72rem',
                    color: '#6d28d9',
                    background: '#f5f3ff',
                    border: '1px solid #ddd6fe',
                    padding: '10px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    <Loader2 size={14} className="spin" />
                    Gerando novo Manual PDF atualizado automaticamente... Por favor, aguarde.
                  </div>
                ) : !pdfUrl ? (
                  <button
                    onClick={() => handleGeneratePdf(false)}
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '0.82rem',
                      color: '#ffffff',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 15px rgba(16, 185, 129, 0.25)',
                      transition: 'all 0.2s',
                      width: '100%'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.filter = 'none'}
                  >
                    <FileText size={15} />
                    Gerar Manual Técnico PDF Oficial
                  </button>
                ) : (
                  <div style={{ fontSize: '0.72rem', color: '#10b981', background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)', padding: '10px', borderRadius: '8px', fontWeight: 600 }}>
                    ✨ Pronto para Publicação! Todos os artefatos de homologação estão gerados e prontos para liberação da versão.
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', width: '100%', boxSizing: 'border-box' }}>
                  <button
                    onClick={() => handleRunSimulation()}
                    style={{
                      flex: 1,
                      background: '#ffffff',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      fontSize: '0.78rem',
                      color: '#475569',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f1f5f9';
                      e.currentTarget.style.borderColor = '#94a3b8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#ffffff';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                  >
                    <RotateCcw size={13} />
                    Nova Simulação
                  </button>

                  <button
                    onClick={() => setSimulationSuccess(null)}
                    style={{
                      flex: 1,
                      background: '#f5f3ff',
                      border: '1px solid #ddd6fe',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      fontSize: '0.78rem',
                      color: '#7c3aed',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#ede9fe';
                      e.currentTarget.style.borderColor = '#c084fc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f5f3ff';
                      e.currentTarget.style.borderColor = '#ddd6fe';
                    }}
                  >
                    Voltar ao Início
                  </button>
                </div>
              </div>
            </div>
          ) : !isSimulating && simulationSuccess === false ? (
            /* FAILURE CARD */
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '20px',
              padding: '35px',
              textAlign: 'center',
              zIndex: 2,
              maxWidth: '520px',
              background: '#ffffff',
              border: '1px solid #ef4444',
              borderRadius: '16px',
              boxShadow: '0 10px 25px rgba(239, 68, 68, 0.05)',
              animation: 'fadeIn 0.3s ease-in-out'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ef4444',
                boxShadow: '0 0 20px rgba(239, 68, 68, 0.15)'
              }}>
                <XCircle size={32} />
              </div>
              <div>
                <h4 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px 0', fontFamily: "'Outfit', sans-serif" }}>
                  Falha na Simulação do Roteiro
                </h4>
                <p style={{ fontSize: '0.78rem', color: '#475569', margin: 0, lineHeight: 1.45 }}>
                  A automação encontrou um erro ou estourou o limite de tempo aguardando um elemento ou mudança de página. Verifique os logs no console acima e ajuste os seletores.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px', width: '100%', boxSizing: 'border-box' }}>
                <button
                  onClick={() => handleRunSimulation()}
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '11px 20px',
                    fontSize: '0.82rem',
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 15px rgba(124, 58, 237, 0.25)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.filter = 'none'}
                >
                  <RotateCcw size={14} />
                  Simular Novamente
                </button>

                <button
                  onClick={() => setSimulationSuccess(null)}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    padding: '11px 20px',
                    fontSize: '0.82rem',
                    color: '#475569',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                >
                  Voltar
                </button>
              </div>
            </div>
          ) : (
            /* IDLE SCREEN / STARTER CARD */
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '20px',
              padding: '40px',
              textAlign: 'center',
              zIndex: 2,
              maxWidth: '520px',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '16px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.02)'
            }}>
              {isSimulating ? (
                <>
                  <Loader2 size={40} className="spin" style={{ color: '#a78bfa' }} />
                  <div>
                    <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', margin: '0 0 6px 0' }}>
                      Conectando ao Simulador...
                    </h4>
                    <p style={{ fontSize: '0.78rem', color: '#475569', margin: 0, lineHeight: 1.4 }}>
                      O navegador está iniciando no servidor. Aguardando o primeiro frame de visualização.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: '#f5f3ff',
                    border: '1px solid #ddd6fe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#7c3aed',
                    boxShadow: '0 0 15px rgba(139, 92, 246, 0.1)'
                  }}>
                    <Play size={28} fill="currentColor" style={{ marginLeft: '4px' }} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px 0', fontFamily: "'Outfit', sans-serif" }}>
                      Simulador Playwright
                    </h4>
                    <p style={{ fontSize: '0.78rem', color: '#475569', margin: 0, lineHeight: 1.4 }}>
                      Execute e homologue visualmente este roteiro no servidor. Você poderá pausar, avançar passo a passo, ver os logs e ouvir a tutoria em tempo real.
                    </p>
                  </div>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    width: '100%',
                    marginTop: '8px'
                  }}>
                    {/* Visivel checkbox */}
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      fontSize: '0.78rem',
                      color: '#475569',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}>
                      <input 
                        type="checkbox" 
                        checked={simulationModeVisible}
                        onChange={(e) => setSimulationModeVisible(e.target.checked)}
                        style={{ accentColor: '#7c3aed', cursor: 'pointer' }}
                      />
                      Modo Visível (Navegador Aberto no Host)
                    </label>

                    <button
                      onClick={() => handleRunSimulation()}
                      disabled={passos.length === 0}
                      style={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px 24px',
                        fontSize: '0.82rem',
                        color: '#ffffff',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 15px rgba(124, 58, 237, 0.3)',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.filter = 'none'}
                    >
                      <Play size={16} fill="currentColor" />
                      Iniciar Simulação do Roteiro
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

        </div>

        {/* 3. Control Toolbar Panel (Fixed at the very bottom of the panel box) */}
        <div style={{
          minHeight: '52px',
          height: '52px',
          background: '#ffffff',
          borderTop: '1px solid #e2e8f0',
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 15,
          position: 'relative'
        }}>
          {/* LADO ESQUERDO: URL do Navegador (Pequeno) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '220px', minWidth: '220px', overflow: 'hidden' }}>
            <span style={{ fontSize: '0.65rem', color: '#64748b', whiteSpace: 'nowrap' }}>URL:</span>
            <span style={{ 
              fontSize: '0.65rem', 
              color: '#475569', 
              textOverflow: 'ellipsis', 
              overflow: 'hidden', 
              whiteSpace: 'nowrap',
              direction: 'rtl',
              textAlign: 'left'
            }} title={currentBrowserUrl}>
              {currentBrowserUrl || 'about:blank'}
            </span>
          </div>

          {/* CENTRO: Controles da Simulação */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Step Backward */}
            <button
              onClick={handleStepBackward}
              disabled={!isSimulating || !isPaused || (activeStepOrdem !== null && activeStepOrdem <= 1)}
              title="Passo Anterior"
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                color: (isSimulating && isPaused && activeStepOrdem !== null && activeStepOrdem > 1) ? '#475569' : '#cbd5e1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: (isSimulating && isPaused && activeStepOrdem !== null && activeStepOrdem > 1) ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s'
              }}
              onMouseEnter={e => {
                if (isSimulating && isPaused && activeStepOrdem !== null && activeStepOrdem > 1) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
              }}
            >
              <SkipBack size={16} />
            </button>

            {/* Play / Pause Toggle */}
            {isPaused ? (
              <button
                onClick={handleResumeSimulation}
                disabled={!isSimulating}
                title="Continuar"
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#10b981',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                }}
                onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
                onMouseLeave={e => e.currentTarget.style.filter = 'none'}
              >
                <Play size={16} fill="currentColor" />
              </button>
            ) : (
              <button
                onClick={handlePauseSimulation}
                disabled={!isSimulating}
                title="Pausar"
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#8b5cf6',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isSimulating ? 'pointer' : 'not-allowed',
                  transition: 'all 0.15s',
                  boxShadow: isSimulating ? '0 2px 8px rgba(139, 92, 246, 0.3)' : 'none'
                }}
                onMouseEnter={e => {
                  if (isSimulating) e.currentTarget.style.filter = 'brightness(1.1)';
                }}
                onMouseLeave={e => e.currentTarget.style.filter = 'none'}
              >
                <Pause size={16} fill="currentColor" />
              </button>
            )}

            {/* Step Forward */}
            <button
              onClick={handleStepForward}
              disabled={!isSimulating || !isPaused || (activeStepOrdem !== null && activeStepOrdem >= passos.length)}
              title="Passo Seguinte"
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                color: (isSimulating && isPaused && activeStepOrdem !== null && activeStepOrdem < passos.length) ? '#475569' : '#cbd5e1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: (isSimulating && isPaused && activeStepOrdem !== null && activeStepOrdem < passos.length) ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s'
              }}
              onMouseEnter={e => {
                if (isSimulating && isPaused && activeStepOrdem !== null && activeStepOrdem < passos.length) {
                  e.currentTarget.style.background = '#f1f5f9';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = '#ffffff';
              }}
            >
              <SkipForward size={16} />
            </button>

            {/* Encerrar (Stop) */}
            <button
              onClick={handleStopSimulation}
              disabled={!isSimulating}
              title="Encerrar Simulação"
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: isSimulating ? '#ef4444' : '#ffffff',
                border: isSimulating ? 'none' : '1px solid #e2e8f0',
                color: isSimulating ? '#ffffff' : '#cbd5e1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isSimulating ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s',
                boxShadow: isSimulating ? '0 2px 8px rgba(239, 68, 68, 0.3)' : 'none'
              }}
              onMouseEnter={e => {
                if (isSimulating) e.currentTarget.style.filter = 'brightness(1.1)';
              }}
              onMouseLeave={e => e.currentTarget.style.filter = 'none'}
            >
              <Square size={14} fill="currentColor" />
            </button>
          </div>

          {/* LADO DIREITO: Instância Telemetry Pill */}
          <div style={{ width: '220px', minWidth: '220px', display: 'flex', justifyContent: 'flex-end', position: 'relative' }}>
            <div 
              onClick={(e) => {
                e.stopPropagation();
                setShowInstanceMenu(!showInstanceMenu);
              }}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: browserInstance?.active ? '#ecfdf5' : '#f8fafc',
                border: browserInstance?.active ? '1px solid #a7f3d0' : '1px solid #e2e8f0',
                borderRadius: '100px',
                padding: '4px 12px',
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'all 0.2s',
                boxShadow: browserInstance?.active ? '0 0 10px rgba(16, 185, 129, 0.02)' : 'none'
              }}
              onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.15)'}
              onMouseLeave={e => e.currentTarget.style.filter = 'none'}
            >
              <div style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: browserInstance?.active ? '#10b981' : '#64748b',
                boxShadow: browserInstance?.active ? '0 0 6px #10b981' : 'none'
              }} />
              <span style={{ 
                fontSize: '0.68rem', 
                fontWeight: 700, 
                color: browserInstance?.active ? '#047857' : '#64748b' 
              }}>
                Instância {browserInstance?.active ? 'Ativa' : 'Inativa'}
              </span>
              <ChevronDown size={11} style={{ color: browserInstance?.active ? '#047857' : '#64748b' }} />

              {/* Dropdown Floating Menu (Abre para CIMA) */}
              {showInstanceMenu && (
                <div style={{
                  position: 'absolute',
                  bottom: 'calc(100% + 8px)',
                  right: 0,
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px',
                  width: '200px',
                  boxShadow: '0 -10px 25px rgba(0, 0, 0, 0.05)',
                  zIndex: 9999,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  textAlign: 'left'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                      Dados da Instância
                    </span>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#475569' }}>
                      <span>Status:</span>
                      <span style={{ fontWeight: 700, color: browserInstance?.active ? '#34d399' : '#f87171' }}>
                        {browserInstance?.active ? 'Quente' : 'Inativo'}
                      </span>
                    </div>
                    {browserInstance?.active && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#475569' }}>
                          <span>Modo:</span>
                          <span style={{ fontWeight: 600 }}>
                            {browserInstance.headless ? 'Oculto (Server)' : 'Visível (Host)'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#475569' }}>
                          <span>Uptime:</span>
                          <span style={{ fontWeight: 700, color: '#7c3aed' }}>{uptimeStr}</span>
                        </div>
                      </>
                    )}
                  </div>

                  <div style={{ height: '1px', background: '#f1f5f9' }} />

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestartBrowser();
                        setShowInstanceMenu(false);
                      }}
                      disabled={!browserInstance?.active}
                      style={{
                        width: '100%',
                        background: 'rgba(139, 92, 246, 0.1)',
                        border: '1px solid rgba(139, 92, 246, 0.25)',
                        borderRadius: '6px',
                        color: '#c084fc',
                        padding: '6px',
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        cursor: browserInstance?.active ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        opacity: browserInstance?.active ? 1 : 0.5
                      }}
                    >
                      <RotateCcw size={12} />
                      Reiniciar Navegador
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleKillBrowser();
                        setShowInstanceMenu(false);
                      }}
                      disabled={!browserInstance?.active}
                      style={{
                        width: '100%',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.25)',
                        borderRadius: '6px',
                        color: '#f87171',
                        padding: '6px',
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        cursor: browserInstance?.active ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        opacity: browserInstance?.active ? 1 : 0.5
                      }}
                    >
                      <Square size={10} fill="currentColor" style={{ color: '#ef4444' }} />
                      Forçar Encerramento
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Custom styles for animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}} />
    </div>
  );
};
